import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import {
  ILocalTrack,
  useConnectionState,
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  useRemoteAudioTracks,
  useRemoteUsers,
  useRemoteVideoTracks,
  useRTCClient,
} from 'agora-rtc-react'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'
import { AgoraLogContext } from 'src/lib/datadog/agoraLogger'
import { AgoraCloudProxyMode } from 'src/lib/gql-client'

import { LocalPlayerContext } from '../../context/localPlayerContext'
import {
  RoomStatus,
  ProxyStatus,
  VideoCallRoomContext,
} from '../../context/videoCallRoomContext'
import { useAgoraLogger } from '../../hooks/useAgoraLogger'
import { useJoinNotificationSound } from '../../hooks/useJoinNotificationSound'
import { useMediaError } from '../../hooks/useMediaError'
import { VideoCallScene } from '../../types'
import { generateChannelName } from '../../utils/generateChannelName'
import {
  extractOriginalUserUidFromScreenShareUserUid,
  isScreenShareUserUid,
} from '../../utils/screenShare'
import { LocalScreenSharePlayer } from '../VideoCallRoomLocalScreenSharePlayer/VideoCallRoomLocalScreenSharePlayer'

type Props = {
  roomUid: string
  token: string | null
  videoCallUserUid: number | null
  screenShareUserUid: number | null
  screenShareToken: string | null
  scene: VideoCallScene
  agoraCloudProxyMode: AgoraCloudProxyMode
  enableJoinSound?: boolean
  isConnectionTest?: boolean
  children: ReactNode
}

export const VideoCallRoomProvider = ({
  roomUid,
  children,
  token,
  screenShareUserUid,
  screenShareToken,
  scene,
  videoCallUserUid,
  agoraCloudProxyMode,
  enableJoinSound = true,
  isConnectionTest = false,
}: Props) => {
  const [roomStatus, setRoomStatus] = useState<RoomStatus>('ready')
  const [proxyStatus, setProxyStatus] = useState<ProxyStatus>('idle')

  // Cloud Proxy使用時はプロキシ準備完了も条件に含める
  const shouldJoin =
    agoraCloudProxyMode === 'Auto' || proxyStatus === 'connected'

  const { error: joinError } = useJoin(
    {
      appid: import.meta.env.VITE_AGORA_APP_ID,
      channel: generateChannelName(roomUid, scene),
      token,
      uid: videoCallUserUid,
    },
    roomStatus === 'joining' && shouldJoin,
  )

  const [localMicrophoneEnabled, setLocalMicrophoneEnabled] = useState(false)
  const [localCameraEnabled, setLocalCameraEnabled] = useState(false)
  const [localScreenEnabled, setLocalScreenEnabled] = useState(false)
  const [currentCameraDeviceId, setCurrentCameraDeviceId] = useState<
    string | null
  >(null)
  const [currentMicrophoneDeviceId, setCurrentMicrophoneDeviceId] = useState<
    string | null
  >(null)
  const [isManuallyChangingCamera, setIsManuallyChangingCamera] =
    useState(false)
  const [isManuallyChangingMicrophone, setIsManuallyChangingMicrophone] =
    useState(false)

  const { localMicrophoneTrack, error: localMicrophoneError } =
    useLocalMicrophoneTrack(localMicrophoneEnabled, {
      ...(currentMicrophoneDeviceId
        ? { microphoneId: currentMicrophoneDeviceId }
        : {}),
      ANS: true, // 自動ノイズ抑制
      AEC: true, // エコーキャンセル
      AGC: true, // 自動ゲイン調整
    })
  const { localCameraTrack, error: localCameraError } = useLocalCameraTrack(
    localCameraEnabled,
    currentCameraDeviceId ? { cameraId: currentCameraDeviceId } : undefined,
  )

  // デバイスIDを初期化・更新（手動切り替え中は自動更新をスキップ）
  useEffect(() => {
    if (isManuallyChangingCamera) return

    if (localCameraTrack) {
      // MediaStreamTrackから直接デバイスIDを取得
      const deviceId = localCameraTrack
        .getMediaStreamTrack()
        ?.getSettings()?.deviceId
      if (deviceId && deviceId !== currentCameraDeviceId) {
        setCurrentCameraDeviceId(deviceId)
      }
    }
  }, [localCameraTrack, currentCameraDeviceId, isManuallyChangingCamera])

  useEffect(() => {
    if (isManuallyChangingMicrophone) return

    if (localMicrophoneTrack) {
      // MediaStreamTrackから直接デバイスIDを取得
      const deviceId = localMicrophoneTrack
        .getMediaStreamTrack()
        ?.getSettings()?.deviceId
      if (deviceId && deviceId !== currentMicrophoneDeviceId) {
        setCurrentMicrophoneDeviceId(deviceId)
      }
    }
  }, [
    localMicrophoneTrack,
    currentMicrophoneDeviceId,
    isManuallyChangingMicrophone,
  ])

  const logContext: AgoraLogContext = useMemo(
    () => ({
      roomUid,
      videoCallUserUid,
      scene,
      agoraCloudProxyMode,
    }),
    [roomUid, videoCallUserUid, scene, agoraCloudProxyMode],
  )

  useMediaError({
    localMicrophoneError,
    localCameraError,
    logContext,
  })

  const client = useRTCClient()
  const connectionState = useConnectionState()

  useAgoraLogger({ client, logContext })

  // Cloud Proxy設定と状態管理
  useEffect(() => {
    // CloudProxyを有効にする場合はjoin前に実行する必要がある
    if (agoraCloudProxyMode !== 'Auto') {
      setProxyStatus('connecting')
      client.startProxyServer(agoraCloudProxyMode === 'ForceUDP' ? 3 : 5)
      setProxyStatus('connected')
    }
  }, [client, agoraCloudProxyMode])

  // agora-rtc-reactが提供するusePublish相当の処理だが、unpublishがうまく動作しないため自前で実装している
  // 原因がわかったらL:86までの範囲を消してusePublish([localMicrophoneTrack, localCameraTrack])に置き換えること
  const handlePublishTrack = useCallback(
    async (track: ILocalTrack | null | undefined, enabled: boolean) => {
      if (!track || connectionState !== 'CONNECTED') return
      if (enabled) {
        await client.publish(track)
      } else {
        await client.unpublish(track)
      }
    },
    [client, connectionState],
  )

  useEffect(() => {
    handlePublishTrack(localMicrophoneTrack, localMicrophoneEnabled)
  }, [localMicrophoneTrack, localMicrophoneEnabled, handlePublishTrack])
  useEffect(() => {
    handlePublishTrack(localCameraTrack, localCameraEnabled)
  }, [localCameraTrack, localCameraEnabled, handlePublishTrack])

  // これらのhooksを呼び出すことでRemoteUserのtrackをsubscribeする
  const remoteUsers = useRemoteUsers()
  useRemoteVideoTracks(remoteUsers)
  useRemoteAudioTracks(remoteUsers)
  useJoinNotificationSound({
    connectionState,
    remoteUsers,
    enabled: enableJoinSound,
  })

  // audioは全員分、常にここで再生する（末端のコンポーネントでAudio系の処理は不要）
  // ただし、自分の画面共有の音声は除外する（二重音声を防ぐため）
  remoteUsers.forEach(user => {
    // 画面共有ユーザーかどうかをチェック
    if (isScreenShareUserUid(user.uid)) {
      try {
        // 画面共有UIDから元のユーザーUIDを抽出
        const originalUserUid = extractOriginalUserUidFromScreenShareUserUid(
          user.uid,
        )
        // 自分の画面共有の場合は音声を再生しない
        if (originalUserUid === videoCallUserUid) {
          return
        }
      } catch {
        // 抽出に失敗した場合はそのまま再生する
      }
    }
    user.audioTrack?.play()
  })

  const join = useCallback(() => {
    setRoomStatus('joining')
  }, [])

  const leave = useCallback(() => {
    setRoomStatus('left')
  }, [])

  const toggleLocalMicrophone = useCallback(() => {
    setLocalMicrophoneEnabled(prev => !prev)
  }, [])

  const toggleLocalCamera = useCallback(() => {
    setLocalCameraEnabled(prev => !prev)
  }, [])

  const startLocalScreen = useCallback(() => {
    setLocalScreenEnabled(true)
  }, [])

  const stopLocalScreen = useCallback(() => {
    setLocalScreenEnabled(false)
  }, [])

  const switchCamera = useCallback(
    async (deviceId: string) => {
      try {
        setIsManuallyChangingCamera(true)

        if (localCameraTrack) {
          // 通話中：リアルタイム切り替え
          await localCameraTrack.setDevice(deviceId)
        }

        // 通話開始前・後共通：状態更新
        setCurrentCameraDeviceId(deviceId)
      } catch (error) {
        console.error('カメラの切り替えに失敗しました:', error)
        throw error
      } finally {
        setIsManuallyChangingCamera(false)
      }
    },
    [localCameraTrack],
  )

  const switchMicrophone = useCallback(
    async (deviceId: string) => {
      try {
        setIsManuallyChangingMicrophone(true)

        if (localMicrophoneTrack) {
          // 通話中：リアルタイム切り替え
          await localMicrophoneTrack.setDevice(deviceId)
        }

        // 通話開始前・後共通：状態更新
        setCurrentMicrophoneDeviceId(deviceId)
      } catch (error) {
        console.error('マイクの切り替えに失敗しました:', error)
        throw error
      } finally {
        setIsManuallyChangingMicrophone(false)
      }
    },
    [localMicrophoneTrack],
  )

  const toast = useMirohaToast()

  const [isForcedExitByOtherDevice, setIsForcedExitByOtherDevice] =
    useState(false)

  const handleForceExit = useCallback(() => {
    setIsForcedExitByOtherDevice(true)
    leave()
  }, [leave])

  useEffect(() => {
    if (joinError) {
      if (!isConnectionTest) {
        setRoomStatus('ready')
      }
      toast({
        status: 'error',
        title: isConnectionTest
          ? 'ビデオ通話ネットワークへの接続に失敗しました'
          : 'ビデオ通話への参加に失敗しました',
        description: '時間をおいて再度お試しください。',
      })
    }
  }, [joinError, toast, isConnectionTest])

  // 同一uidユーザーが入室した際に元のユーザーを強制退出させるための処理
  client.on('connection-state-change', (state, prevState, reason) => {
    if (
      state === 'DISCONNECTED' &&
      prevState === 'CONNECTED' &&
      reason === 'UID_BANNED'
    ) {
      handleForceExit()
    }
    // 強制退出した後に再度接続した場合はflagをリセットする
    if (state === 'CONNECTED' && isForcedExitByOtherDevice) {
      setIsForcedExitByOtherDevice(false)
    }
  })

  return (
    <VideoCallRoomContext.Provider
      value={{
        roomStatus,
        connectionState,
        isForcedExitByOtherDevice,
        videoCallRoomUid: roomUid,
        videoCallUserUid,
        proxyStatus,
        agoraCloudProxyMode,
        join,
        leave,
      }}
    >
      <LocalPlayerContext.Provider
        value={{
          localMicrophoneEnabled,
          localCameraEnabled,
          localScreenEnabled,
          localMicrophoneTrack,
          localCameraTrack,
          localMicrophoneError,
          localCameraError,
          currentCameraDeviceId,
          currentMicrophoneDeviceId,
          toggleLocalCamera,
          toggleLocalMicrophone,
          startLocalScreen,
          stopLocalScreen,
          switchCamera,
          switchMicrophone,
        }}
      >
        {localScreenEnabled && !!screenShareUserUid && (
          <LocalScreenSharePlayer
            token={screenShareToken}
            roomUid={roomUid}
            videoCallUserUid={screenShareUserUid}
            scene={scene}
          />
        )}
        {children}
      </LocalPlayerContext.Provider>
    </VideoCallRoomContext.Provider>
  )
}
